const _ = require('lodash')
const Promise = require('bluebird')

// Allow this library to be loaded as a no-op on the server-side
const airbrakeClient = typeof window !== 'undefined'
  ? window.airbrake
  : undefined

module.exports = {
  configure,
  attachBuildNumber,
  addFilter,
  notify
}

function configure() {
  if (!airbrakeClient) {
    return
  }
  filterIgnorableErrors()
}

function addFilter(filter) {
  if (airbrakeClient) {
    airbrakeClient.addFilter(filter)
  }
}

function notify(notice) {
  if (airbrakeClient) {
    return airbrakeClient.notify(notice)
    .then(notice => {
      if (notice.id) {
        // eslint-disable-next-line no-console
        console.log('Airbrake notified:', notice.id);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Airbrake failed:', notice.error);
      }
    })
  }
  // eslint-disable-next-line no-console
  console.warn(`Airbrake is not enabled:`, notice)
  return Promise.resolve()
}

function attachBuildNumber(buildNumber) {
  addFilter(notice => {
    notice.buildNumber = buildNumber
    return notice
  })
}

function filterIgnorableErrors() {
  addFilter(notice => {
    const [ err ] = notice.errors
    if (isIgnorableError(err)) {
      return null
    }
    return notice
  })
}

function isIgnorableError(err) {
  return _.some(IGNORABLE_ERROR_TESTS, test => test(err))
}

function isIgnorableValue(errorValue, testValue) {
  if (_.isArray(testValue)) {
    return _.some(testValue, i => isIgnorableValue(errorValue, i))
  }
  if (_.isRegExp(testValue)) {
    return testValue.test(errorValue)
  }
  if (_.isString(testValue)) {
    return testValue === errorValue
  }
  return false
}

const IGNORABLE_ERROR_TESTS = [
  // Errors in browser extensions (except our own)
  err => isIgnorableValue(err.backtrace[0].file, [
    /^chrome-extension:\/\/(?!jdgmgdpcphmdofoplgjifmdajnfojnin)/,
    /^moz-extension:\/\/(?!7217e373-875a-cc46-92ab-5b02a45c6e99)/,
    /^safari-extension:\/\/(?!com\.mixmedia\.mix\.mac\.app)/,
    /^javascript:/
  ]),
  // Known SecurityError caused by user-agent preferences
  err => _.every([
    isIgnorableValue(err.backtrace[0].file, [
      /^https:\/\/cdn\.branch\.io/,
      /^https:\/\/connect\.facebook\.net/
    ]),
    err.message === 'The operation is insecure.'
  ]),
  // Third-party Iterate script errors
  err => err.backtrace[0].file === 'webpack:///../web/scripts/src/common/util.js',
  // Non-Error thrown/rejected values with no meaningful stacktrace
  err => _.every([
    err.backtrace[0].file === 'webpack://airbrakeJs.Client/./src/client.ts',
    isIgnorableValue(err.message, [
      '[object Object]',
      'unhandled rejection with no reason given',
      'Permission denied to access property "reason"'
    ])
  ]),
  // Known non-actionable XHR errors
  err => _.every([
    err.backtrace[0].file === 'webpack:///./client/lib/request.js',
    isIgnorableValue(err.message, [
      'invalid csrf token',
      'Rate limit exceeded',
      // https://app.asana.com/0/102902769366319/690838729515894/f
      'XHR status[404] for url[https://suggestqueries.google.com/complete/search]',
      // Aborted requests are not really errors.
      /^XHR statusText\[abort\]/,
      // Unsent requests generally mean client-side network hiccups.
      /^XHR readyState\[UNSENT\]/,
      // Don't kick ourselves while we're down for 5xx errors on root-relative urls
      /^XHR status\[5[0-9]{2}\] for url\[\//
    ])
  ]),
  // Known third-party extension injected script errors
  err => _.every([
    err.backtrace[0].function === '',
    err.message === `can't redefine non-configurable property "userAgent"`
  ]),
  err => _.every([
    err.backtrace[0].file === '',
    isIgnorableValue(err.message, [
      'tgetT is not defined',
      `Cannot set property 'install' of undefined`,
      `Cannot read property 'checkDomStatus' of undefined`,
      `Cannot read property 'DOMNodeInsertedByJs' of undefined`,
      `Cannot read property 'end' of undefined`,
      `Failed to read the 'cssRules' property from 'CSSStyleSheet': Cannot access rules`,
      /^Blocked a frame with origin "[^"]+" from accessing/,
      /^[A-Za-z0-9]{4}\.data\.indexOf is not a function$/,
      /^(WeixinJSBridge|vid_mate_\S+|__show__deepen) is not defined$/
    ])
  ]),
  err => _.every([
    err.backtrace[0].function === 'global code',
    isIgnorableValue(err.message, [
      `Can't find variable: pktAnnotationHighlighter`,
      `Can't find variable: Ruby_ExtractLoginFormData`,
      `Can't find variable: article`,
      `null is not an object (evaluating 'article.element')`
    ])
  ])
]
